import type {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteParams,
  RouteRecordRaw
} from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useGlobalStore } from '@/stores/global'

import AppLayout from '@/layouts/AppLayout.vue'

export type AppRouteNames =
  | 'home'
  | 'about'
  | 'login'
  | 'email-verify'
  | 'password-reset'
  | 'host'
  | 'season'
  | 'week'

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      title: 'One Team Poker',
      layout: AppLayout
    }
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/AboutView.vue'),
    meta: {
      // requiresAuth: true,
      title: 'About',
      layout: AppLayout
    }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      title: 'Login',
      layout: AppLayout
    }
  },
  // {
  //   path: '/logout',
  //   name: 'logout',
  //   component: {
  //     beforeRouteEnter(to, from, next) {
  //       console.log({ from });
  //       logout()
  //       next('home');
  //     }
  //   }
  // },
  {
    path: '/email/verify/:accountId/:code',
    name: 'email-verify',
    component: () => import('@/views/EmailVerificationView.vue'),
    meta: {
      title: 'Email Verification',
      layout: AppLayout
    }
  },
  {
    path: '/reset-password',
    name: 'password-reset',
    component: () => import('@/views/PasswordResetView.vue'),
    meta: {
      title: 'Password Reset',
      layout: AppLayout
    }
  },
  {
    path: '/resend-verification-email',
    name: 'resend-verify',
    component: () => import('@/views/ResendVerificationView.vue'),
    meta: {
      title: 'Password Reset',
      layout: AppLayout
    }
  },
  {
    path: '/host/:id',
    name: 'host',
    component: () => import('@/views/pages/HostView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Host',
      layout: AppLayout
    }
  },
  {
    path: '/season/:id',
    name: 'season',
    component: () => import('@/views/pages/SeasonView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Season',
      layout: AppLayout
    }
  },
  {
    path: '/week/:id',
    name: 'week',
    component: () => import('@/views/pages/WeekView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Week',
      layout: AppLayout
    }
  }
]

// export const routes: RouteRecordRaw[] = [
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes
})

export function logout() {
  const userStore = useUserStore()
  userStore.logout()
}

export function routerPush(
  name: AppRouteNames,
  params?: RouteParams
): ReturnType<typeof router.push> {
  return params === undefined ? router.push({ name }) : router.push({ name, params })
}

// Global before guards
// https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards}
router.beforeEach(
  async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const globalStore = useGlobalStore()
    const userStore = useUserStore()

    // if (configStore.appUnderMaintenance && to.name !== 'maintenance') {
    //   globalStore.addSnackbarMessageToQueue('The site is currently in maintenance mode.', 'error')
    //   next({ name: 'maintenance' })
    //   return
    // }

    // Show Loading
    // comment out for https://github.com/logue/vite-vuetify-ts-starter/issues/16
    // globalStore.setLoading(true);
    globalStore.setMessage('')
    // if the route is a guarded route - then we should check to see if they're logged in ..
    if (to.meta.requiresAuth && !userStore.isLoggedin) {
      next({ name: 'login', query: { redirect: to.path } })
    } else {
      globalStore.incrementLoading()
      next()
    }

    // Hide snack bar
  }
)

// Global After Hooks
// https://router.vuejs.org/guide/advanced/navigation-guards.html#global-after-hooks}
router.afterEach((to) => {
  const globalStore = useGlobalStore()
  // Hide Loading
  globalStore.decrementLoading()
  document.title = to.meta.title ?? import.meta.env.VITE_APP_TITLE
})

/*
const scrollBehavior = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  savedPosition: RouteLocation
): Promise<any> => {
  let scrollpos = {};
  if (to.hash) {
    scrollpos = {
      el: to.hash,
      behavior: 'smooth',
    };
  } else if (savedPosition) {
    scrollpos = savedPosition;
  } else {
    scrollpos = { top: 0 };
  }
  return await new Promise((resolve, _reject) => {
    setTimeout(() => {
      resolve(scrollpos);
    }, 600);
  });
};
*/

export default router
